import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEOHead } from "@/components/SEOHead";
import logoFreelaDash from "@/assets/logo-freeladash.png";

export default function TermsPage() {
  return (
    <>
      <SEOHead 
        title="Termos de Uso"
        description="Termos de uso do FreelaDash. Leia nossos termos e condições de utilização da plataforma."
        canonical="/termos"
      />
      <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto flex h-14 items-center justify-between px-4 sm:h-16">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src={logoFreelaDash} 
              alt="FreelaDash" 
              className="h-8 w-8 rounded-lg object-contain"
            />
            <span className="font-display text-xl font-bold tracking-wide text-foreground" style={{ WebkitTextStroke: '0.5px currentColor' }}>
              FREELADASH
            </span>
          </Link>
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-8 sm:py-12">
        <article className="mx-auto max-w-3xl">
          <h1 className="mb-2 text-3xl font-bold sm:text-4xl">TERMOS DE USO</h1>
          <p className="mb-8 text-sm text-muted-foreground">Última atualização: 06 de fevereiro de 2026</p>

          <div className="prose prose-gray max-w-none space-y-6 text-foreground">
            <section>
              <h2 className="text-xl font-semibold text-foreground">1. Aceitação dos Termos</h2>
              <p className="text-muted-foreground leading-relaxed">
                Ao acessar ou utilizar o FreelaDash ("Plataforma"), você concorda em cumprir e estar vinculado a estes Termos de Uso. 
                Se você não concordar com qualquer parte destes termos, não deve utilizar nossa plataforma.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground">2. Descrição do Serviço</h2>
              <p className="text-muted-foreground leading-relaxed">
                O FreelaDash é uma plataforma SaaS (Software as a Service) de gestão para freelancers que oferece ferramentas para:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>Gerenciamento de projetos e tarefas</li>
                <li>Controle de tempo (timesheet)</li>
                <li>Cadastro e gestão de clientes</li>
                <li>Controle financeiro básico e pagamentos</li>
                <li>Geração de relatórios</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground">3. Cadastro e Conta</h2>
              <p className="text-muted-foreground leading-relaxed">
                Para utilizar o FreelaDash, você deve criar uma conta através do login via Google OAuth. 
                Você é responsável por manter a confidencialidade de sua conta e por todas as atividades realizadas através dela. 
                Você concorda em fornecer informações verdadeiras, precisas e completas durante o cadastro.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground">4. Planos e Pagamentos</h2>
              <p className="text-muted-foreground leading-relaxed">
                O FreelaDash oferece um plano gratuito com funcionalidades limitadas e um plano Pro com recursos completos. 
                O plano Pro é cobrado mensalmente através da plataforma Stripe. Os valores e condições de pagamento estão 
                disponíveis na página de preços da plataforma.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Os pagamentos são processados de forma segura pela Stripe, e o FreelaDash não armazena dados de cartão de crédito.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground">5. Uso Aceitável</h2>
              <p className="text-muted-foreground leading-relaxed">Você concorda em não:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>Violar qualquer lei ou regulamento aplicável</li>
                <li>Tentar acessar contas de outros usuários</li>
                <li>Interferir no funcionamento da plataforma</li>
                <li>Transmitir vírus ou código malicioso</li>
                <li>Utilizar a plataforma para fins ilegais ou não autorizados</li>
                <li>Revender ou redistribuir o serviço sem autorização</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground">6. Propriedade Intelectual</h2>
              <p className="text-muted-foreground leading-relaxed">
                Todo o conteúdo da plataforma, incluindo design, código, textos, gráficos, logos e marcas, 
                são de propriedade exclusiva do FreelaDash e protegidos por leis de direitos autorais. 
                Os dados inseridos por você permanecem de sua propriedade.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground">7. Limitação de Responsabilidade</h2>
              <p className="text-muted-foreground leading-relaxed">
                O FreelaDash é fornecido "como está" e "conforme disponível". Não garantimos que o serviço será 
                ininterrupto, livre de erros ou completamente seguro. Em nenhuma circunstância seremos responsáveis 
                por danos indiretos, incidentais, especiais ou consequenciais, incluindo perda de lucros, dados ou 
                oportunidades de negócio.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Nossa responsabilidade total em relação a qualquer reclamação não excederá o valor pago por você 
                nos últimos 12 meses de uso do serviço.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground">8. Disponibilidade do Serviço</h2>
              <p className="text-muted-foreground leading-relaxed">
                Nos esforçamos para manter o FreelaDash disponível 24 horas por dia, 7 dias por semana, 
                mas não garantimos disponibilidade ininterrupta. Podemos realizar manutenções programadas 
                ou de emergência quando necessário, com ou sem aviso prévio.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground">9. Exclusão de Conta</h2>
              <p className="text-muted-foreground leading-relaxed">
                Você pode solicitar a exclusão de sua conta a qualquer momento através das configurações da plataforma 
                ou entrando em contato conosco. Após a exclusão, seus dados serão removidos de nossos sistemas conforme 
                nossa Política de Privacidade.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground">10. Alterações nos Termos</h2>
              <p className="text-muted-foreground leading-relaxed">
                Reservamo-nos o direito de modificar estes Termos de Uso a qualquer momento. Alterações significativas 
                serão comunicadas por email ou através de aviso na plataforma. O uso continuado do serviço após as 
                alterações constitui aceitação dos novos termos.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground">11. Legislação Aplicável</h2>
              <p className="text-muted-foreground leading-relaxed">
                Estes Termos são regidos pelas leis da República Federativa do Brasil. Qualquer disputa será resolvida 
                nos tribunais competentes da comarca de São Paulo, SP.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground">12. Contato</h2>
              <p className="text-muted-foreground leading-relaxed">
                Para dúvidas sobre estes Termos de Uso, entre em contato:
              </p>
              <p className="text-muted-foreground">
                <strong className="text-foreground">Email:</strong>{" "}
                <a href="mailto:jmkoygg@gmail.com" className="text-primary hover:underline">
                  jmkoygg@gmail.com
                </a>
              </p>
            </section>
          </div>
        </article>
      </main>

      {/* Footer */}
      <footer className="border-t py-6 bg-card mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          © 2026 FreelaDash. Todos os direitos reservados.
        </div>
      </footer>
    </div>
    </>
  );
}
